import { FC } from 'react';
import { z } from 'zod';
import { IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../../../../shared/fe/hooks/useForm';
import { useApiMutation } from '../../../../../query/hooks';
import { useModalContext } from '../../../../../modal/ModalContext';
import { graphql } from '../../../../../gql';
import { ModalContent } from '../../../../../modal/components/ModalContent';
import { FormField } from '../../../../../form/FormField';
import { ModalActions } from '../../../../../modal/components/ModalActions';
import { OutlinedInput } from '@mui/material';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useSearchParams } from 'react-router-dom';
import qs from 'qs';
import { useSnackbar } from '../../../../../store/snackbar';

const createActionAndAddToActivityModal_createAction = graphql(`
	mutation createActionAndAddToActivityModal_createAction(
		$name: String!
		$description: String
		$priority: ActionPriority!
		$status: ActionStatus!
		$links: Json!
	) {
		createAction(data: { name: $name, description: $description, priority: $priority, status: $status, links: $links }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const createActionAndAddToActivityModal_updateActivity = graphql(`
	mutation createActionAndAddToActivityModal_updateActivity($activityId: UUID!, $actionId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { actions: { connect: { id: $actionId } } }) {
			ok
			errorMessage
		}
	}
`);

const createActionAndAddToActivityModalSchema = z.object({
	activityId: z.string(),
});

type Props = z.infer<typeof createActionAndAddToActivityModalSchema>;

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'opportunity.detail.name.error.empty-name' })),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const CreateActionAndAddToActivityModal: FC<Props> = ({ activityId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const { mutateAsync: createAction, isPending: isCreateActionPending } = useApiMutation(createActionAndAddToActivityModal_createAction);
	const { mutateAsync: updateActivity, isPending: isUpdateActivityPending } = useApiMutation(
		createActionAndAddToActivityModal_updateActivity,
	);
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			name: '',
		},
	});

	const submitDisabled = !form.formState.isValid || isCreateActionPending || isUpdateActivityPending;

	const onSubmit = async ({ name }: FormValues) => {
		let result = true;
		let error = '';
		let errorMessage: string | null | undefined = '';

		const createResult = await createAction({
			name,
			description: '',
			priority: 'medium',
			status: 'open',
			links: { values: [] },
		});

		if (!createResult.createAction.ok) {
			result = false;
			error = intl.formatMessage({ id: 'action.list.create.error' });
			errorMessage = createResult.createAction.errorMessage;
		}

		const actionId = createResult.createAction.node?.id;

		if (actionId) {
			const updateResult = await updateActivity({ activityId, actionId });

			if (!updateResult.updateActivity.ok) {
				result = false;
				error = intl.formatMessage({ id: 'journey.detail.widget.actions.add.error' });
				errorMessage = updateResult.updateActivity.errorMessage;
			}
		}

		mutationResponseHandler({
			result,
			toastMessages: {
				success: intl.formatMessage({ id: 'action.list.create.success' }),
				error,
			},
			errorMessage,
			openSnackbar,
			callback: () => {
				/**
				 * NOTE: `react-router-dom`'s `setSearchParams` function claims to be an updater function but isn't.
				 * If we dispatch two searchParam changes at once (closeModal and setDrawer),
				 * the output of the first call isn't fed into the second.
				 * We solve this by writing a `closeModal` + `setDrawer` combination here manually.
				 */

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { modal, ...rest } = qs.parse(searchParams.toString());
				setSearchParams(qs.stringify({ ...rest, drawer: { type: 'editAction', actionId } }));
			},
		});
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'journey.detail.widget.actions.create-modal.title' })} description={''}>
				<form noValidate>
					<FormField
						form={form}
						name={'name'}
						label={intl.formatMessage({ id: 'journey.detail.widget.actions.create-modal.name-label' })}
						render={(props) => <OutlinedInput {...props} />}
					/>
				</form>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'journey.detail.widget.actions.create-modal.submit' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};

export { CreateActionAndAddToActivityModal, createActionAndAddToActivityModalSchema };
